// The sprint view's table (`/sprints`): Fit · Engmt (with scores on) · Account · Owner · First touch ·
// Last touch · Replies · Meetings · Opportunities. Fit, Engmt and Account are the board's own
// columns and hovers; the rest read the book's per-account outreach, with the rules in
// `sprintTotals.ts`, so a row and the totals over it always agree.

import type { BookColumn } from './columns';
import { Dash, boardV2Columns, compactScoreColumns } from './boardV2Columns';
import { creditedOpenDeals, creditedPipeline } from './sprintTotals';
import { firstName, formatCount, formatMoney, formatShortDay } from '../lib/format';

function DayCell({ day }: { day: string | null }) {
  return day ? <span className="dclip">{formatShortDay(day)}</span> : <Dash />;
}

const OWNER_COLUMN: BookColumn = {
  key: 'sdr_owner',
  label: 'Owner',
  width: '96px',
  hint: 'The strategist who owns the account (Salesforce SDR owner).',
  sortValue: row => row.sdr_owner || null,
  accessor: row => row.sdr_owner || '',
  cell: row => (row.sdr_owner ? <span className="dclip" title={row.sdr_owner}>{firstName(row.sdr_owner)}</span> : <Dash />),
};

const FIRST_TOUCH_COLUMN: BookColumn = {
  key: 'first_touch',
  label: 'First touch',
  width: '104px',
  hint: 'The owner’s first touch on the account, any channel.',
  sortValue: row => row.first_touch,
  accessor: row => row.first_touch ?? '',
  cell: row => <DayCell day={row.first_touch} />,
};

const LAST_TOUCH_COLUMN: BookColumn = {
  key: 'last_touch',
  label: 'Last touch',
  width: '104px',
  hint: 'The owner’s latest touch on the account, any channel.',
  sortValue: row => row.last_touch,
  accessor: row => row.last_touch ?? '',
  cell: row => <DayCell day={row.last_touch} />,
};

// The owner's replies; "Others" when only someone else at Cartesia got one (the Reply filter's two readings).
function replyText(responses: number, anyResponses: number): string {
  if (responses > 0) return formatCount(responses);
  if (anyResponses > 0) return 'Others';
  return '';
}

const REPLIES_COLUMN: BookColumn = {
  key: 'responses',
  label: 'Replies',
  width: '78px',
  numeric: true,
  hint: 'Replies to the owner, auto-replies excluded. “Others”: only someone else at Cartesia got a reply.',
  sortValue: row => (row.responses ?? 0) * 2 + ((row.any_responses ?? 0) > 0 ? 1 : 0),
  accessor: row => replyText(row.responses ?? 0, row.any_responses ?? 0),
  cell: row => {
    const text = replyText(row.responses ?? 0, row.any_responses ?? 0);
    return text ? <span className="dclip">{text}</span> : <Dash />;
  },
};

const MEETINGS_COLUMN: BookColumn = {
  key: 'meetings',
  label: 'Meetings',
  width: '96px',
  numeric: true,
  hint: 'Meetings counted for the owner, manual meetings included; else the next one booked.',
  sortValue: row => (row.meetings ?? 0) * 2 + (row.next_meeting ? 1 : 0),
  accessor: row => String(row.meetings ?? 0),
  cell: row => {
    if ((row.meetings ?? 0) > 0) return <span className="dclip">{formatCount(row.meetings)}</span>;
    if (row.next_meeting) return <span className="dclip" title="Booked ahead">Next {formatShortDay(row.next_meeting)}</span>;
    return <Dash />;
  },
};

const OPPORTUNITIES_COLUMN: BookColumn = {
  key: 'credited_opportunities',
  label: 'Opportunities',
  width: '120px',
  numeric: true,
  hint: 'Open deals created on or after the owner’s first sent touch, and their qualified pipeline (set from stage 2).',
  sortValue: row => creditedPipeline(row) + creditedOpenDeals(row).length,
  accessor: row => String(creditedOpenDeals(row).length),
  cell: row => {
    const deals = creditedOpenDeals(row);
    if (deals.length === 0) return <Dash />;
    const pipeline = creditedPipeline(row);
    const stages = deals.map(deal => deal.stage || 'No stage').join(', ');
    return (
      <span className="dclip" title={stages}>
        {formatCount(deals.length)}{pipeline > 0 ? ` · ${formatMoney(pipeline)}` : ''}
      </span>
    );
  },
};

export function sprintColumns(scoresShown: boolean): BookColumn[] {
  const account = boardV2Columns(true).find(column => column.key === 'account')!;
  return [
    ...(scoresShown ? compactScoreColumns() : []),
    account,
    OWNER_COLUMN,
    FIRST_TOUCH_COLUMN,
    LAST_TOUCH_COLUMN,
    REPLIES_COLUMN,
    MEETINGS_COLUMN,
    OPPORTUNITIES_COLUMN,
  ];
}
